<!--
  @component Navbar.MegaMenu

  A mega menu component for the navbar that displays additional content when activated.
  It handles mouse enter/leave and focus/blur events for smooth interaction.

  @prop colorVariant - The color variant for the mega menu background and text
  @prop css - Custom CSS styles to apply to the mega menu

  Example:
  <Navbar.MegaMenu colorVariant="primary">
    <Navbar.MegaMenuColumn>
      <Navbar.MegaMenuHeader title="Products" href="/products" />
      <Navbar.MegaMenuLink href="/product1" label="Product 1" />
      <Navbar.MegaMenuLink href="/product2" label="Product 2" />
    </Navbar.MegaMenuColumn>
  </Navbar.MegaMenu>
-->

<script lang="ts">
  import { css } from '@agapi/ui/panda/css';
  import { megaMenuStyles } from '../navbar.styles';
  import { getContext } from 'svelte';
  import { fly } from 'svelte/transition';
  import { dynamicColorStyles } from '@agapi/ui/panda/utils';
  import type { NavbarStore } from '../navbar-store.svelte';
  import type { NavbarItemContext, NavbarMegaMenuProps } from '../navbar.types';

  /** Props for the Navbar.MegaMenu component. */
  const { children, colorVariant, css: cssProp }: NavbarMegaMenuProps = $props();

  /** Navbar context containing state and methods. */
  const navbarContext = getContext<NavbarStore>('navbar');
  /** Context for the parent Navbar.Item component. */
  const navbarItemContext = getContext<NavbarItemContext>('navbar-item');

  /**
   * Handles the mouse enter event for the mega menu.
   * Clears any existing close timeout and sets a new timeout to open the menu.
   */
  const handleMouseEnter = () => {
    if (navbarContext.closeTimeoutId) {
      clearTimeout(navbarContext.closeTimeoutId);
      navbarContext.setCloseTimeout(null);
    }

    const timeout = setTimeout(() => {
      navbarContext.setOpenItem(navbarItemContext.id);
    }, navbarItemContext.hoverDelay);

    navbarContext.setHoverTimeout(timeout as unknown as number);
  }

  /**
   * Handles the mouse leave event for the mega menu.
   * Clears any existing hover timeout and sets a new timeout to close the menu.
   */
  const handleMouseLeave = () => {
    if (navbarContext.hoverTimeoutId) {
      clearTimeout(navbarContext.hoverTimeoutId);
      navbarContext.setHoverTimeout(null);
    }

    const timeout = setTimeout(() => {
      navbarContext.setOpenItem(null);
    }, navbarItemContext.closeDelay);

    navbarContext.setCloseTimeout(timeout as unknown as number);
  }

  /** Determines if the mega menu is currently open. */
  const isOpen = $derived(navbarContext.openItemId === navbarItemContext.id);
  /** Styles for the mega menu, dependent on its open state. */
  const styles = $derived(megaMenuStyles.raw({ open: isOpen }));
</script>

{#if isOpen}
  <nav
    class={css(styles.root, cssProp)}
    onmouseenter={handleMouseEnter}
    onmouseleave={handleMouseLeave}
    onfocus={handleMouseEnter}
    onblur={handleMouseLeave}
    tabindex="-1"
    id={`megamenu-${navbarItemContext.id}`}
    in:fly={{ duration: 200, y: navbarContext.direction === 'down' ? -10 : 0, x: navbarContext.direction === 'left' ? -10 : navbarContext.direction === 'right' ? 10 : 0 }}
    out:fly={{ duration: 200, y: navbarContext.direction === 'down' ? -10 : 0, x: navbarContext.direction === 'left' ? 10 : navbarContext.direction === 'right' ? -10 : 0 }}
    style="
    {colorVariant ? `background-color: ${dynamicColorStyles(colorVariant).backgroundColor};` : ''}
    {colorVariant ? `color: ${dynamicColorStyles(colorVariant).color};` : ''}
    "
  >
    {#if children}
      {@render children()}
    {/if}
</nav>
{/if}